import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL, STATUS_LABELS, MONTHS, formatDateOnly } from "./constants/adminConstants";
import AdminChangeDetail from "./AdminChangeDetail";
import "./css/adminStyles.css";

function AdminStaffChanges() {
  const [changeRequests, setChangeRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState({});
  const [noteInputs, setNoteInputs] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);
  const [statusFilter, setStatusFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  useEffect(() => {
    fetchPendingChanges();
  }, []);

  useEffect(() => {
    let filtered = [...changeRequests];

    if (statusFilter) {
      filtered = filtered.filter((cr) => cr.status === statusFilter);
    }

    if (monthFilter) {
      filtered = filtered.filter((cr) => {
        const d = cr.createdAt ? new Date(cr.createdAt) : null;
        return d && d.getMonth() === parseInt(monthFilter);
      });
    }

    if (yearFilter) {
      filtered = filtered.filter((cr) => {
        const d = cr.createdAt ? new Date(cr.createdAt) : null;
        return d && d.getFullYear() === parseInt(yearFilter);
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (cr) =>
          (cr.staffFirstName || "").toLowerCase().includes(q) ||
          (cr.staffLastName || "").toLowerCase().includes(q) ||
          (cr.description || "").toLowerCase().includes(q)
      );
    }

    setFilteredRequests(filtered);
  }, [statusFilter, monthFilter, yearFilter, searchQuery, changeRequests]);

  const fetchPendingChanges = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/manager/pending-changes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setChangeRequests(data.changeRequests || []);
        setFilteredRequests(data.changeRequests || []);
      }
    } catch (err) {
      console.error("Fetch staff changes error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/manager/change-requests/${id}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ note: noteInputs[id] || "" }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Change approved and sent to admin.");
        fetchPendingChanges();
      } else {
        setMessage(data.error || "Failed to approve.");
      }
    } catch (err) {
      setMessage("Server error.");
    }
  };

  const handleReject = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/manager/change-requests/${id}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ note: noteInputs[id] || "" }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Change rejected.");
        fetchPendingChanges();
      } else {
        setMessage(data.error || "Failed to reject.");
      }
    } catch (err) {
      setMessage("Server error.");
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending_manager": return "AdminBadgePending";
      case "pending_admin": return "AdminBadgePending";
      case "manager_approved": return "AdminBadgeManagerApproved";
      case "admin_approved": return "AdminBadgeAdminApproved";
      case "rejected": return "AdminBadgeRejected";
      default: return "AdminBadgePending";
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!expandedData[id]) {
      const token = sessionStorage.getItem("tvc_token");
      try {
        const res = await fetch(`${API_BASE_URL}/manager/change-requests/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.changeRequest) {
          setExpandedData((prev) => ({ ...prev, [id]: data.changeRequest }));
        }
      } catch (err) {
        console.error("Fetch change request detail error:", err);
      }
    }
  };

  const handleNoteChange = (id, value) => {
    setNoteInputs((prev) => ({ ...prev, [id]: value }));
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);

  if (loading) {
    return <div className="AdminEmptyState">Loading staff changes...</div>;
  }

  return (
    <div>
      {message && (
        <div style={{ marginBottom: 16 }}>
          <p className={message.includes("error") || message.includes("Failed") ? "AdminError" : "AdminSuccess"}>
            {message}
          </p>
        </div>
      )}

      <div className="AdminCard">
        <div className="AdminCardHeader">
           <h3 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 20, fontWeight: 700, color: "#1B2A4A" }}>Staff Change Requests <span className="AdminCountBadge">{filteredRequests.length}</span></h3>
          <div className="AdminPillFilterBar">
            <div className="AdminPillGroup">
              <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search staff, description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                   onBlur={() => { setSearchQuery(""); setTimeout(() => setSearchOpen(false), 200) }}
                />
                {!searchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
            </div>

            <div className="AdminPillGroup">
              <div className="AdminPillFilterItem">
                <select className="AdminPillSelect" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All Statuses</option>
                  <option value="pending_manager">Pending Manager</option>
                  <option value="pending_admin">Manager made the Changes</option>
                  <option value="manager_approved">Manager Approved</option>
                  <option value="admin_approved">Admin Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="AdminPillFilterItem">
                <select className="AdminPillSelect" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
                  <option value="">Months</option>
                  {MONTHS.map((m, i) => (
                    <option key={i} value={i}>{m}</option>
                  ))}
                </select>
                {monthFilter && (
                  <button className="AdminPillClear" onClick={() => setMonthFilter("")}>&times;</button>
                )}
              </div>

              <div className="AdminPillFilterItem">
                <select className="AdminPillSelect" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                  <option value="">Years</option>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {yearFilter && (
                  <button className="AdminPillClear" onClick={() => setYearFilter("")}>&times;</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="AdminEmptyState">No staff change requests found.</div>
        ) : (
          <div className="AdminTableScroll">
          <table className="AdminTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Staff</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((cr) => (
                <React.Fragment key={cr.id}>
                  <tr>
                    <td>{formatDateOnly(cr.createdAt)}</td>
                    <td>{cr.staffFirstName} {cr.staffLastName}</td>
                    <td style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {cr.description}
                    </td>
                    <td>
                      <span className={`AdminBadge ${getStatusBadgeClass(cr.status)}`}>
                        {STATUS_LABELS[cr.status] || cr.status}
                      </span>
                    </td>
                    <td>
                      <div className="AdminActionsCell">
                        <button className="AdminExpandBtn" onClick={() => toggleExpand(cr.id)}>
                          {expandedId === cr.id ? "Hide" : "View"}
                        </button>
                        {cr.status === "pending_manager" && (
                          <>
                            <button className="AdminIconBtn AdminIconBtnGreen" title="Approve" onClick={() => handleApprove(cr.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            </button>
                            <button className="AdminIconBtn AdminIconBtnRed" title="Reject" onClick={() => handleReject(cr.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === cr.id && (
                    <tr>
                      <td colSpan={5} style={{ padding: "16px 24px", background: "#f8fafc" }}>
                        {expandedData[cr.id] ? (
                          <AdminChangeDetail changeRequest={expandedData[cr.id]} />
                        ) : (
                          <div className="AdminEmptyState">Loading change details...</div>
                        )}
                        <div style={{ marginTop: 12 }}>
                          <label className="AdminLabel">Note (optional)</label>
                          <textarea
                            className="AdminTextarea"
                            placeholder="Add a note..."
                            value={noteInputs[cr.id] || ""}
                            onChange={(e) => handleNoteChange(cr.id, e.target.value)}
                          />
                        </div>
                        {cr.status === "pending_manager" && (
                          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                            <button className="AdminIconBtn AdminIconBtnGreen" title="Approve" onClick={() => handleApprove(cr.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            </button>
                            <button className="AdminIconBtn AdminIconBtnRed" title="Reject" onClick={() => handleReject(cr.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminStaffChanges;
